/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable, Injector } from '@angular/core';
import { DiConfigService } from '@app/di/di-config.service';
import { UtilService } from '@app/util/util.service';
import { NotifyService } from '@app/common/notify.service';
import { MessageService } from '@core/lib/services/message.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { DiService } from '@app/di/di.service';
import { ContextService } from '@app/di/context.service';
import { Events, EventService } from '@app/common/event';
import { LineShaftIntegration, LineShaftHandlerService } from '@app/di/channel/lineshaft/line-shaft-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '@app/common/app-config.service';

const log = new Logger('UniversalViewerContextService');

@Injectable({
  providedIn: 'root'
})
export class UniversalViewerContextService implements LineShaftIntegration {
  private lineShaftHandlerService: LineShaftHandlerService;
  private VIEWER_CONTEXT_CONFIG;
  private UVIEWER_CONFIG;
  private uvSync;

  constructor(
    private injector: Injector,
    private diConfigService: DiConfigService,
    private translate: TranslateService,
    private utilService: UtilService,
    private notify: NotifyService,
    private messageService: MessageService,
    private eventService: EventService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private integrationStatusService: IntegrationStatusService,
    private diService: DiService,
    private contextService: ContextService,
    private appConfig: AppConfigService
  ) {}

  initialize() {
    log.info('App Initializing...');
    // Communication channel
    this.lineShaftHandlerService = this.injector.get(LineShaftHandlerService);
    this.lineShaftHandlerService.registerCleanupCallback(this.cleanup);

    // Configs
    this.VIEWER_CONTEXT_CONFIG = this.diConfigService.VIEWER_CONTEXT_CONFIG;
    this.UVIEWER_CONFIG = this.diConfigService.UVIEWER_CONFIG;
    this.uvSync = this.VIEWER_CONTEXT_CONFIG.UV_SYNC;

    // Listeners
    this.broadcastListeners();
    this.eventListeners();
  }

  isViewerContextConfigInvalid() {
    let invalidConfig = false;
    if (_.isEmpty(this.VIEWER_CONTEXT_CONFIG.MODULENAME) || _.isUndefined(this.VIEWER_CONTEXT_CONFIG.MODULENAME)) {
      log.error('isViewerContextConfigInvalid - Viewer context plugin name is empty or undefined.');
      invalidConfig = true;
    }
    return invalidConfig;
  }

  subscribePluginResponse(retObj) {
    try {
      if (this.diService.hasXMLConfError('ViewerContext') || this.isViewerContextConfigInvalid()) {
        this.integrationStatusService.setUViewerNavSyncPluginStatus(true);
        this.integrationStatusService.setViewerSyncHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
        log.error('Incorrect ViewerContext plugin configuration - ViewerContext plugin disabled');
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.incorrectViewerContextPluginConfig'),
          this.translate.instant('di.desktopIntegration.titleViewerContextPluginDisabled')
        );
      } else {
        // Subscribe the load response from ViewerContext plugin
        const viewerContextPluginLoadResponse = this.xmlTemplateManagerService.getSubscribeXml(
          'LS_PLUGIN_LOAD_RESPONSE',
          this.VIEWER_CONTEXT_CONFIG.MODULENAME + '*' + this.VIEWER_CONTEXT_CONFIG.MODULENAME
        );
        log.info('Subscribe the load response from ViewerContext plugin');
        this.diService.loadPluginsInvokeAPIWrapperExecute(viewerContextPluginLoadResponse);
        // Subscribe response from LineShaftPluginManager to get load response error due to the missing of
        // ViewerContext plugin
        const viewerContextPluginLoadResponseFromPluginManager = this.xmlTemplateManagerService.getSubscribeXml(
          this.VIEWER_CONTEXT_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_LOAD_RESPONSE',
          'LineShaftPluginManager'
        );
        this.diService.loadPluginsInvokeAPIWrapperExecute(viewerContextPluginLoadResponseFromPluginManager);
        // Subscribe the Comparison_Context response from Viewer Context plugin
        const comparisonContextSubcribeResponse = this.xmlTemplateManagerService.getSubscribeXml(
          'LS_TOPIC_COMPARISON_CONTEXT',
          this.VIEWER_CONTEXT_CONFIG.MODULENAME
        );
        log.info('Subscribe the Comparison_Context response from Viewer Context plugin');
        this.diService.loadPluginsInvokeAPIWrapperExecute(comparisonContextSubcribeResponse);

        // Subscribe the active study context response from ViewerContextPlugin
        const activeStudyContextResponse = this.xmlTemplateManagerService.getSubscribeXml(
          'LineShaftPluginManager.ACTIVE_STUDY_CONTEXT',
          this.VIEWER_CONTEXT_CONFIG.MODULENAME
        );
        log.info('Subscribe the active study context response from ViewerContextPlugin');
        this.diService.loadPluginsInvokeAPIWrapperExecute(activeStudyContextResponse);
      }
    } catch (errorSubscribeViewerContextPlugin) {
      retObj.loadErrorEntity.push('ViewerContextPlugin');
      log.error('subscribePluginResponse() ViewerContextPlugin :' + errorSubscribeViewerContextPlugin);
    }
  }

  loadPlugin(retObj?: any) {
    if (
      !_.contains(retObj.loadErrorEntity, 'ViewerContextPlugin') &&
      !this.isViewerContextConfigInvalid() &&
      !this.diService.hasXMLConfError('ViewerContext')
    ) {
      try {
        const viewerContextPluginLoadXML = this.xmlTemplateManagerService.getPluginLoadXml('ViewerContextPlugin');
        log.info('load ViewerContext plugin');
        const viewerContextPluginLoadMessage = this.diService.getLSMessage(
          this.diService.getLSMessageType().LOAD,
          viewerContextPluginLoadXML
        );
        viewerContextPluginLoadMessage.responseSource = [
          this.VIEWER_CONTEXT_CONFIG.MODULENAME + '*' + this.VIEWER_CONTEXT_CONFIG.MODULENAME,
          this.UVIEWER_CONFIG.PLUGIN_MANAGER
        ];
        viewerContextPluginLoadMessage.moduleName = this.VIEWER_CONTEXT_CONFIG.MODULENAME;
        this.diService.loadPluginsInvokeAPIWrapper(viewerContextPluginLoadMessage);
      } catch (errorLoadViewerContextPlugin) {
        retObj.loadErrorEntity.push('ViewerContextPlugin');
        log.error('loadPlugins() ViewerContextPlugin :' + errorLoadViewerContextPlugin);
      }
    }
  }

  viewerContextPluginHandler(response) {
    if (response.changeType === 'SYNCHRONIZATION_ERROR') {
      log.error('UV Navigator Response with SYNCHRONIZATION_ERROR ' + response.changeDescription);
    } else {
      const navigatorData = this.parseViewerContextPluginResponse(response);
      if (navigatorData.length > 0 && response.changeDescription !== undefined) {
        log.info('broadcast syncUvNavigatorWithDxHubComparison event');
        this.messageService.broadcast('syncUvNavigatorWithDxHubComparison', navigatorData);
      }
    }
  }

  parseViewerContextPluginResponse(response) {
    const navigatorData = [];
    const length = response.studyID.length;
    for (let i = 0; i < length; i++) {
      const responseData = response.studyID[i].split('|');
      const responseJson = {} as any;
      for (const data of responseData) {
        const nodeName = data.split(':')[0];
        switch (nodeName) {
          case 'NCD':
            const procId = data.split(':')[1];
            responseJson.procedureId = isNaN(procId) ? procId : parseInt(procId, 10);
            break;
          case 'ACTIVE':
            responseJson.hasOpenedImage = UtilService.convertToBool(data.split(':')[1]);
            break;
          case 'CURRENT':
            responseJson.current = UtilService.convertToBool(data.split(':')[1]);
            break;
          case 'ARCHIVESTATUS':
            responseJson.archiveStatus = data.split(':')[1];
            break;
          case 'IMAGECOUNT':
            responseJson.imageCount = parseInt(data.split(':')[1], 10);
            break;
          default:
            break;
        }
      }
      navigatorData.push(responseJson);
    }
    return navigatorData;
  }

  cleanup = (): boolean => {
    try {
      log.info('CleanUp - Started');
      // viewerContext plugin close xml should send even if load is failed, so no need to check load status.
      // If the close xml is not send on load failure(i.e. CLOSE handler not executed) next time LOAD xml
      // won't reach in viewerContext plugin. Create the close xml and close viewerContext plugin
      const viewerContextCloseXML = this.xmlTemplateManagerService.getPluginCloseXml(
        this.VIEWER_CONTEXT_CONFIG.MODULENAME
      );
      this.diService.invokeAPIWrapperExecute(viewerContextCloseXML);
      return true;
    } catch (error) {
      log.error('CleanUp - Error >> ', error);
      return false;
    }
  };

  publishViewerContextRequest(data) {
    const primaryStudy = data.primaryStudy;
    const changeDescription = data.actionType;
    const studyList = [];
    const openItems = data.openItems;
    studyList.push(primaryStudy);

    let shouldPublish = false;
    if (!_.isUndefined(changeDescription)) {
      _.each(openItems, (item: any) => {
        studyList.push('NCD:' + item.id);
      });

      const tab = this.utilService.getActiveTab();
      if (tab && this.isViewerLaunched(tab) && tab.comparisons && tab.comparisons.differentPatient.length > 0) {
        _.each(tab.comparisons.differentPatient, (differentPatient: any) => {
          studyList.push('NCD:' + differentPatient.id);
        });
      }

      shouldPublish =
        (changeDescription === 'STUDY_LOADED' && studyList.length > 1) || changeDescription === 'STUDY_UNLOADED';
    }

    if (shouldPublish) {
      this.openCloseComparisonInUvNavigator({
        changeType: 'NAVIGATION',
        changeDescription,
        studyList,
        primaryStudy
      });
    }
  }

  isViewerLaunched(tab) {
    if (tab && (this.integrationStatusService.isViewerPending() || this.contextService.isViewerOpened(tab.id))) {
      return true;
    }
  }

  openCloseComparisonInUvNavigator(data) {
    const contextInfo = this.contextService.getCurrentContextItem();
    if (contextInfo === null) {
      return;
    }
    if (data) {
      const xmlData = {
        contextIdent: contextInfo.token,
        changeType: data.changeType,
        changeDescription: data.changeDescription,
        listOfStudies: data.studyList.join(';'),
        studiesLength: data.studyList.length,
        primaryStudy: data.primaryStudy
      };
      const viewerContextXml = this.xmlTemplateManagerService.getViewerContextXml(xmlData);
      log.info('Viewer Context Plugin SET CONTEXT XML:::::::: \n ' + viewerContextXml);
      this.diService.invokeAPI(viewerContextXml);
    }
  }

  private zeroImageOutOfSync() {
    const contextInfo = this.contextService.getCurrentContextItem();
    if (contextInfo !== null) {
      this.messageService.broadcast('studyOutOfSync');
    }
  }

  private parseViewerContextResponse(response, responseXML) {
    let studyIdIterator = 0;
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseXML, 'text/xml');
      response.dataNodes = xmlDoc.getElementsByTagNameNS('http://ws.dotmed.gehc.com/dataexchange/', 'data');
      for (const dataNode of response.dataNodes) {
        // Take the name nodes from the xml
        const nameNode = dataNode.childNodes.item(1).childNodes[0].nodeValue;
        if (dataNode.childNodes.item(9) !== null && dataNode.childNodes.item(9).childNodes[0] !== undefined) {
          const nodeValue = dataNode.childNodes.item(9).childNodes[0].nodeValue;
          if (nameNode === 'CHANGE_TYPE') {
            response.changeType = nodeValue;
          }
          if (nameNode === 'CHANGE_DESCRIPTION') {
            response.changeDescription = nodeValue;
          }
          if (nameNode === 'OWNERINSTANCE') {
            response.pid = nodeValue;
            const contextInfo = this.contextService.getCurrentContextItem();
            if (contextInfo) {
              contextInfo.ownerInstance = nodeValue;
            }
          }
        }
        if (nameNode === 'LIST_OF_STUDIES') {
          const studyCount = parseInt(dataNode.childNodes.item(5).childNodes[0].nodeValue, 10);
          // Updating failure count with failure type length.
          // Add the failure types in to an array
          for (let studyIterator = 9; studyIdIterator < studyCount; studyIterator = studyIterator + 2) {
            response.studyID[studyIdIterator] = dataNode.childNodes.item(studyIterator).childNodes[0].nodeValue;
            studyIdIterator = studyIdIterator + 1;
          }
        }
        if (nameNode === 'LIST_OF_REMOTE_STUDIES') {
          studyIdIterator = 0;
          const remoteStudyCount = parseInt(dataNode.childNodes.item(5).childNodes[0].nodeValue, 10);
          for (let iterator = 9; studyIdIterator < remoteStudyCount; iterator = iterator + 2) {
            response.remoteStudyID[studyIdIterator] = dataNode.childNodes.item(iterator).childNodes[0].nodeValue;
            studyIdIterator = studyIdIterator + 1;
          }
        }
      }
    } catch (error) {
      log.error('parseViewerContextResponse failed. Error = ' + error);
    }
    return response;
  }

  private displayNotificationMsgDetailsInSystemStatus(key, message) {
    this.appConfig.getSystemStatusNotifier().next({ key, message });
  }
  /**
   * Method to handle ViewerContext plugin load failure from LS plugin manager.
   *
   */
  private handleViewerContextPluginLoadFailureFromPM() {
    this.displayNotificationMsgDetailsInSystemStatus(
      'keyViewerContextPluginLoadFail',
      this.translate.instant('systemStatusMsg.viewerContextPluginLoadFail')
    );
    log.error(
      'handleViewerContextPluginLoadFailureFromPM - ViewerContext plugin load failed and ' +
        'ViewerContext plugin integration disabled'
    );
    if (!this.integrationStatusService.getUViewerNavSyncPluginStatus()) {
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.loadViewerContextPluginFailed'),
        this.translate.instant('di.desktopIntegration.titleViewerContextPluginDisabled')
      );
    }
    this.integrationStatusService.setViewerSyncHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    this.integrationStatusService.setUViewerNavSyncPluginStatus(true);
  }

  /**
   * Method to handle load failure from LS plugin manager.
   * responseInfo - response information
   */
  LSPluginManagerResponseHandler(responseInfo) {
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager' &&
      responseInfo.resultValue === 'FAILURE' &&
      responseInfo.moduleName === 'ViewerContextPlugin'
    ) {
      log.error('LSPluginManagerResponseHandler - Load plugin failed');
      this.handleViewerContextPluginLoadFailureFromPM();
    }
  }

  /**
   * Method to handle ViewerContext plugin load response.
   */
  private handleViewerContextPluginLoadResponse(responseInfo) {
    if (responseInfo.resultValue === 'SUCCESS') {
      if (this.integrationStatusService.getUViewerNavSyncPluginStatus()) {
        log.info('handleViewerContextPluginLoadResponse :' + ' ViewerContext plugin load success after timeout');
        this.notify.success(
          this.translate.instant('di.desktopIntegration.success.loadViewerContextPluginSuccessAfterTimeout'),
          this.translate.instant('di.desktopIntegration.titleViewerContextPluginEnabled')
        );
      }
      log.info('handleViewerContextPluginLoadResponse : ViewerContext plugin load success');
      this.integrationStatusService.setUViewerNavSyncPluginStatus(true);
      this.integrationStatusService.setViewerSyncHealthStatus(this.integrationStatusService.healthStatusEnum.ready);
    } else {
      log.error(
        'handleViewerContextPluginLoadResponse - ViewerContext plugin load failed -' +
          ' Failure reason : ' +
          responseInfo.resultMsg
      );
      this.integrationStatusService.setViewerSyncHealthStatus(this.integrationStatusService.healthStatusEnum.failed);
    }
  }

  LsPluginLoadResponseHandler(responseInfo) {
    // ViewerContextPlugin Load response
    if (
      responseInfo.topicName === 'LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'ViewerContextPlugin*ViewerContextPlugin'
    ) {
      this.handleViewerContextPluginLoadResponse(responseInfo);
    }
  }

  /** Section : handler functions */
  // Base functions
  private initializeLineShaftViewerContextPlugin() {
    this.utilService.loopUntilSuccessOrMaxRetry((lastAttempt: boolean) => {
      const retObj = { loadErrorEntity: [] };
      this.subscribePluginResponse(retObj);
      this.loadPlugin(retObj);

      if (lastAttempt && retObj.loadErrorEntity.length !== 0) {
        // If ViewerContext plugin load failed, display critical error message
        log.error('loadPluginHandler - ViewerContext plugin load failed');
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.loadViewerContextPluginFailed'),
          this.translate.instant('di.desktopIntegration.titleViewerContextPluginDisabled')
        );
      }
      return retObj.loadErrorEntity.length === 0;
    }, this.VIEWER_CONTEXT_CONFIG.MODULENAME);
  }

  private broadcastListeners() {
    this.lineShaftHandlerService.channelObservers.onReady.subscribe(startLoad => {
      if (startLoad) {
        log.info('LineShaft registration started');
        this.initializeLineShaftViewerContextPlugin();
      }
    });
    this.lineShaftHandlerService.channelObservers.onResponseMessage.subscribe(({ responseInfo, responseXML }) => {
      log.debug('LineShaft message processing', { responseInfo, responseXML });
      if (responseInfo.topicName === 'LS_TOPIC_COMPARISON_CONTEXT' && this.uvSync === 'true') {
        this.viewerContextPluginHandler(this.parseViewerContextResponse(responseInfo, responseXML));
      }
      if (responseInfo.topicName === 'LineShaftPluginManager.ACTIVE_STUDY_CONTEXT') {
        if (responseInfo.contextController !== this.VIEWER_CONTEXT_CONFIG.OWNERINSTANCE) {
          log.error('Out of context response received from ViewerContextPlugin');
          this.messageService.broadcast('ContextMismatch.OutofContext', true);
        } else {
          log.info('Active context response received from ViewerContextPlugin');
          this.messageService.broadcast('ContextMismatch.OutofContext', false);
        }
      }

      this.LSPluginManagerResponseHandler(responseInfo);
      this.LsPluginLoadResponseHandler(responseInfo);
    });
    this.messageService.on('publishViewerContextRequest').subscribe(eventData => {
      if (this.uvSync === 'true' && eventData) {
        this.publishViewerContextRequest(eventData);
      }
    });
    this.messageService.on('zeroImageOutOfSync').subscribe(() => {
      this.zeroImageOutOfSync();
    });
    this.messageService.on('resetPlugin').subscribe(() => {
      log.info('LineShaft restart - Resetting ViewerContextPlugin status');
      this.integrationStatusService.setViewerSyncHealthStatus('init');
      this.integrationStatusService.setUViewerNavSyncPluginStatus(false);
    });
  }

  private eventListeners() {
    this.eventService.afterEvent(Events.launchreadonly.name, target => {
      if (target.keys.source === 'diagnostic-hub' && this.uvSync === 'true') {
        log.info('remove comparison tiles on launching viewer via View Images button');
        this.messageService.broadcast('syncUvNavigatorWithDxHubComparison', []);
      }
    });
  }
}
